import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { API, C } from '../constants';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const NATIVE_INCOMPATIBLE = new Set(['avi', 'mkv', 'mov']);

// Inject spinner keyframes once
if (!document.getElementById('vp-keyframes')) {
  const s = document.createElement('style');
  s.id = 'vp-keyframes';
  s.textContent = '@keyframes vp-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

const S = {
  overlay: { position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(8,4,2,0.93)', gap:'12px', padding:'28px', textAlign:'center', zIndex:20 },
  icon: { fontSize:'28px', lineHeight:1 },
  title: { color:'#e8d5c4', fontSize:'14px', fontFamily:'Inter, sans-serif', fontWeight:'600' },
  sub: { color:'rgba(200,180,160,0.55)', fontSize:'12px', fontFamily:'Inter, sans-serif', maxWidth:'320px', lineHeight:'1.6' },
  retryBtn: { marginTop:'4px', padding:'7px 22px', borderRadius:'8px', border:`1px solid ${C.accentBorder}`, background:C.accentDim, color:C.accentSoft, fontSize:'13px', fontFamily:'Inter, sans-serif', cursor:'pointer', fontWeight:'500' },
  spinner: { width:'44px', height:'44px', borderRadius:'50%', border:'4px solid rgba(201,113,74,0.2)', borderTopColor:'rgba(201,113,74,0.85)', animation:'vp-spin 0.75s linear infinite' },
};

export default function VideoPlayer({ src, hlsBase, videoRef, onLoadedMetadata, onPause, onEnded, style, subtitles, transcodeStatus }) {
  const internalRef  = useRef(null);
  const hlsRef       = useRef(null);
  const recoveredRef = useRef(false);
  const ref          = videoRef || internalRef;

  const [levels, setLevels]             = useState([]);
  const [currentLevel, setCurrentLevel] = useState(-1);
  const [speed, setSpeed]               = useState(1);
  const [showQuality, setShowQuality]   = useState(false);
  const [showSpeed, setShowSpeed]       = useState(false);
  const [pipSupported, setPipSupported] = useState(false);
  const [inPip, setInPip]               = useState(false);
  const [videoError, setVideoError]     = useState(null);
  const [buffering, setBuffering]       = useState(false);
  const [retryKey, setRetryKey]         = useState(0);

  // Derive whether the source format requires HLS to play in browser
  const srcExt = src ? decodeURIComponent(src.split('/').pop()).split('.').pop().toLowerCase() : '';
  const needsHLS = NATIVE_INCOMPATIBLE.has(srcExt);

  const isConverting    = !hlsBase && needsHLS && (transcodeStatus === 'pending' || transcodeStatus === 'processing');
  const isTranscodeErr  = !hlsBase && needsHLS && transcodeStatus === 'error';
  const showOverlay     = isConverting || isTranscodeErr || !!videoError;

  // Reset all playback state when video source changes
  useEffect(() => {
    setVideoError(null);
    setLevels([]);
    setCurrentLevel(-1);
    setBuffering(false);
    recoveredRef.current = false;
  }, [src, hlsBase]);

  // Pause video whenever an overlay is active
  useEffect(() => {
    const video = ref.current;
    if (video && showOverlay) video.pause();
  }, [showOverlay, ref]);

  const retry = useCallback(() => {
    setVideoError(null);
    setBuffering(false);
    recoveredRef.current = false;
    setRetryKey(k => k + 1);
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || isConverting || isTranscodeErr) return;

    const m3u8 = hlsBase ? `${API}/hls/${hlsBase}/master.m3u8` : null;

    setPipSupported(!!(document.pictureInPictureEnabled && !video.disablePictureInPicture));
    video.playbackRate = speed;

    const onPipEnter = () => setInPip(true);
    const onPipExit  = () => setInPip(false);
    const onWaiting  = () => setBuffering(true);
    const onPlaying  = () => { setBuffering(false); };
    const onCanPlay  = () => setBuffering(false);

    video.addEventListener('enterpictureinpicture', onPipEnter);
    video.addEventListener('leavepictureinpicture', onPipExit);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplay', onCanPlay);

    if (m3u8 && Hls.isSupported()) {
      recoveredRef.current = false;
      const hls = new Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        enableWorker: true,
        fragLoadingRetryDelay: 1000,
        manifestLoadingRetryDelay: 1000,
        maxFragLoadingRetry: 3,
        maxManifestLoadingRetry: 2,
      });
      hlsRef.current = hls;
      let retryTimer = null;
      hls.loadSource(m3u8);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setLevels(hls.levels.map((l, i) => ({ index: i, height: l.height })));
        setCurrentLevel(-1);
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recoveredRef.current) {
          // First media error: attempt automatic recovery
          recoveredRef.current = true;
          hls.recoverMediaError();
        } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !recoveredRef.current) {
          // First network error: retry loading after 1s
          recoveredRef.current = true;
          retryTimer = setTimeout(() => hls.startLoad(), 1000);
        } else {
          setVideoError('Error al cargar el stream. Verifica tu conexión e intenta de nuevo.');
        }
      });

      return () => {
        clearTimeout(retryTimer);
        hls.destroy();
        hlsRef.current = null;
        cleanup();
      };
    } else if (m3u8 && video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = m3u8;
    } else {
      video.src = src;
    }

    return cleanup;

    function cleanup() {
      video.removeEventListener('enterpictureinpicture', onPipEnter);
      video.removeEventListener('leavepictureinpicture', onPipExit);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplay', onCanPlay);
    }
  }, [src, hlsBase, retryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQuality = useCallback((level) => {
    if (hlsRef.current) hlsRef.current.currentLevel = level;
    setCurrentLevel(level);
    setShowQuality(false);
  }, []);

  const setPlaybackSpeed = useCallback((s) => {
    if (ref.current) ref.current.playbackRate = s;
    setSpeed(s);
    setShowSpeed(false);
  }, [ref]);

  const togglePiP = useCallback(async () => {
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else if (ref.current) await ref.current.requestPictureInPicture();
    } catch {}
  }, [ref]);

  const qualityLabel = currentLevel === -1
    ? 'Auto'
    : (levels[currentLevel]?.height ? `${levels[currentLevel].height}p` : 'Auto');

  const hasHls = hlsBase && levels.length > 0;

  const barBtn = (label, active, onClick) => (
    <button onClick={onClick} style={{ padding:'5px 11px', borderRadius:'6px', border:`1px solid ${active ? C.accentBorder : 'rgba(255,255,255,0.12)'}`, background: active ? C.accentDim : 'rgba(255,255,255,0.06)', color: active ? C.accentSoft : C.textMuted, fontSize:'12px', cursor:'pointer', fontFamily:'Inter, sans-serif', fontWeight:'500', whiteSpace:'nowrap' }}>
      {label}
    </button>
  );

  const { borderRadius, boxShadow, maxHeight, ...videoStyle } = style || {};

  return (
    <div style={{ borderRadius: borderRadius || '14px', overflow:'hidden', boxShadow: boxShadow || 'none', background:'#000', display:'block', position:'relative' }}>

      {/* Overlays */}
      {isConverting && (
        <div style={S.overlay}>
          <span style={S.icon}>⏳</span>
          <span style={S.title}>Convirtiendo a formato web…</span>
          <span style={S.sub}>Este proceso puede tomar varios minutos. La calidad HD estará disponible pronto.</span>
        </div>
      )}
      {isTranscodeErr && (
        <div style={S.overlay}>
          <span style={S.icon}>✕</span>
          <span style={S.title}>Error al procesar el video</span>
          <span style={S.sub}>Este archivo no pudo convertirse al formato web. El administrador debe revisar el archivo original.</span>
        </div>
      )}
      {videoError && !isConverting && !isTranscodeErr && (
        <div style={S.overlay}>
          <span style={S.icon}>⚠</span>
          <span style={S.title}>{videoError}</span>
          <button onClick={retry} style={S.retryBtn}>Reintentar</button>
        </div>
      )}

      {/* Buffering spinner */}
      {buffering && !showOverlay && (
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none', zIndex:10 }}>
          <div style={S.spinner} />
        </div>
      )}

      <video
        ref={ref}
        controls
        autoPlay
        onLoadedMetadata={onLoadedMetadata}
        onPause={onPause}
        onEnded={onEnded}
        onError={e => {
          const code = e.target.error?.code;
          if (code === 2) setVideoError('Error de red al cargar el video.');
          else if (code === 3) setVideoError('Error al decodificar el video. El archivo puede estar dañado.');
          else if (code === 4) setVideoError('Formato no compatible con el navegador. Espera a que termine la conversión HLS.');
        }}
        controlsList="nodownload"
        onContextMenu={e => e.preventDefault()}
        crossOrigin="anonymous"
        style={{ width:'100%', maxHeight: maxHeight || '560px', display:'block', ...videoStyle, borderRadius:0, boxShadow:'none', visibility: showOverlay ? 'hidden' : 'visible' }}
      >
        {Array.isArray(subtitles) && subtitles.map(s => (
          <track key={s.id} kind="subtitles"
            src={`${API}/subtitles/${encodeURIComponent(s.file)}`}
            srcLang={s.lang || 'es'} label={s.label || s.file}
          />
        ))}
      </video>

      {/* Controls bar */}
      <div style={{ background:'rgba(8,4,2,0.95)', padding:'7px 14px', display:'flex', alignItems:'center', gap:'8px', borderTop:`1px solid rgba(201,113,74,0.12)`, flexWrap:'wrap' }}
        onClick={() => { setShowQuality(false); setShowSpeed(false); }}>

        {hasHls && (
          <div style={{ position:'relative' }}>
            {barBtn(`⚙ ${qualityLabel}`, currentLevel !== -1, e => { e.stopPropagation(); setShowQuality(q => !q); setShowSpeed(false); })}
            {showQuality && (
              <div style={{ position:'absolute', bottom:'calc(100% + 6px)', left:0, background:'rgba(15,10,8,0.98)', border:`1px solid ${C.accentBorder}`, borderRadius:'8px', overflow:'hidden', zIndex:50, minWidth:'90px', boxShadow:'0 8px 24px rgba(0,0,0,0.7)' }}
                onClick={e => e.stopPropagation()}>
                {[{ index: -1, label: 'Auto' }, ...levels.map(l => ({ index: l.index, label: l.height ? `${l.height}p` : `Nivel ${l.index}` }))].reverse().map(l => (
                  <div key={l.index} onClick={() => setQuality(l.index)}
                    style={{ padding:'8px 14px', color: currentLevel === l.index ? C.accentSoft : C.text, background: currentLevel === l.index ? C.accentDim : 'transparent', cursor:'pointer', fontSize:'12px', fontFamily:'Inter, sans-serif', whiteSpace:'nowrap' }}
                    onMouseEnter={e => { if (currentLevel !== l.index) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                    onMouseLeave={e => { if (currentLevel !== l.index) e.currentTarget.style.background = 'transparent'; }}>
                    {l.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ position:'relative' }}>
          {barBtn(`${speed}×`, speed !== 1, e => { e.stopPropagation(); setShowSpeed(s => !s); setShowQuality(false); })}
          {showSpeed && (
            <div style={{ position:'absolute', bottom:'calc(100% + 6px)', left:0, background:'rgba(15,10,8,0.98)', border:`1px solid ${C.accentBorder}`, borderRadius:'8px', overflow:'hidden', zIndex:50, minWidth:'80px', boxShadow:'0 8px 24px rgba(0,0,0,0.7)' }}
              onClick={e => e.stopPropagation()}>
              {SPEEDS.map(s => (
                <div key={s} onClick={() => setPlaybackSpeed(s)}
                  style={{ padding:'8px 14px', color: speed === s ? C.accentSoft : C.text, background: speed === s ? C.accentDim : 'transparent', cursor:'pointer', fontSize:'12px', fontFamily:'Inter, sans-serif' }}
                  onMouseEnter={e => { if (speed !== s) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { if (speed !== s) e.currentTarget.style.background = 'transparent'; }}>
                  {s}×
                </div>
              ))}
            </div>
          )}
        </div>

        {pipSupported && barBtn(inPip ? '⊡ PiP ✓' : '⊡ PiP', inPip, e => { e.stopPropagation(); togglePiP(); })}

        <span style={{ marginLeft:'auto', color:'rgba(92,74,60,0.7)', fontSize:'10px', fontFamily:'Inter, sans-serif', letterSpacing:'0.3px' }}>
          Space · ←/→ 10s · ↑/↓ vol · F pantalla completa · M mute
        </span>
      </div>
    </div>
  );
}
